import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';
import { s_TI, s_PB, s_GB } from '../../shared/styles';
import {
  getMembers,
  getContracts, getContract, saveContract, deleteContract,
  getLeaveRequests, submitLeaveRequest, reviewLeaveRequest, deleteLeaveRequest,
  getSickReports, fileSickReport, markRecovered,
  getLeaveBalance,
  updateMemberHrRole,
} from './businessApi';

// Style helpers
const btnPrimary  = (x={}) => s_PB({ fontSize: 13, padding: '8px 16px', borderRadius: 10, ...x });
const btnGhost    = (x={}) => s_GB({ fontSize: 13, padding: '8px 16px', borderRadius: 10, ...x });
const btnDanger   = (x={}) => ({ ...s_PB({ fontSize: 13, padding: '8px 16px', borderRadius: 10, ...x }), background: '#ef4444', color: '#fff' });
const inputStyle  = (x={}) => s_TI({ fontSize: 14, padding: '10px 14px', ...x });
const labelStyle  = { color: C.silver, fontSize: 12, marginBottom: 4, display: 'block' };

const CONTRACT_TYPES = [
  { value: 'fulltime',  nl: 'Fulltime',  en: 'Full-time' },
  { value: 'parttime',  nl: 'Parttime',  en: 'Part-time' },
  { value: 'flex',      nl: 'Flexibel',  en: 'Flexible' },
  { value: 'freelance', nl: 'Freelance', en: 'Freelance' },
];

const LEAVE_TYPES = [
  { value: 'vacation',   nl: 'Vakantie',            en: 'Vacation' },
  { value: 'sick',       nl: 'Ziek',                en: 'Sick' },
  { value: 'medical',    nl: 'Doktersbezoek',        en: 'Medical visit' },
  { value: 'special',    nl: 'Bijzonder verlof',     en: 'Special leave' },
  { value: 'unpaid',     nl: 'Onbetaald verlof',     en: 'Unpaid leave' },
];

const LEAVE_STATUS_COLORS = {
  pending:  '#f59e0b',
  approved: '#10b981',
  declined: '#ef4444',
};

const HR_ROLES = [
  { value: '',             nl: 'Geen HR-rol',   en: 'No HR role' },
  { value: 'accountant',  nl: 'Accountant',    en: 'Accountant' },
  { value: 'bookkeeper',  nl: 'Boekhouder',    en: 'Bookkeeper' },
  { value: 'hr_manager',  nl: 'HR-manager',    en: 'HR Manager' },
];

// Module-level language toggle — set once per render from the lang prop
let _isNl = true;
const t   = (nlStr, enStr) => _isNl ? nlStr : enStr;
const fmt = (d) => d ? new Date(d).toLocaleDateString(_isNl ? 'nl-NL' : 'en-GB') : '—';
const fmtMoney = (v) => v != null ? `€ ${parseFloat(v).toLocaleString(_isNl ? 'nl-NL' : 'en-GB', { minimumFractionDigits: 2 })}` : '—';

// ─── Wettelijke vakantiedagen calculator ────────────────────────────────────
const calcWettelijk = (hoursPerWeek) => {
  if (!hoursPerWeek) return 0;
  return Math.round(4 * (hoursPerWeek / 8) * 10) / 10;
};

// ─── Sub-components ─────────────────────────────────────────────────────────

function MemberPicker({ members, value, onChange, placeholder }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{...inputStyle(), width: '100%'}}
    >
      <option value="">{placeholder || t('Selecteer medewerker', 'Select employee')}</option>
      {members.map(m => (
        <option key={m.user_id} value={m.user_id}>
          {m.display_name || m.email}
        </option>
      ))}
    </select>
  );
}

// ─── Tab: Contracten ─────────────────────────────────────────────────────────
function ContractTab({ businessId, myRole, members }) {
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null); // member user_id
  const [contract, setContract]  = useState(null);
  const [editing, setEditing]    = useState(false);
  const [form, setForm]          = useState({});
  const [saving, setSaving]      = useState(false);
  const [msg, setMsg]            = useState('');

  const canEdit = ['owner','admin','hr_manager','accountant','bookkeeper'].includes(myRole);

  const loadContracts = useCallback(async () => {
    const r = await getContracts(businessId);
    if (Array.isArray(r)) setContracts(r);
  }, [businessId]);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  const selectMember = async (uid) => {
    setSelected(uid);
    setEditing(false);
    setMsg('');
    if (!uid) { setContract(null); return; }
    const r = await getContract(businessId, uid);
    setContract(r && !r.error ? r : null);
    if (r && !r.error) {
      setForm({
        contract_type: r.contract_type || 'fulltime',
        hours_per_week: r.hours_per_week || 40,
        salary: r.salary || '',
        start_date: r.start_date?.slice(0,10) || '',
        end_date: r.end_date?.slice(0,10) || '',
        cao: r.cao || '',
        vacation_extra: r.vacation_extra || 0,
        notes: r.notes || '',
      });
    } else {
      setForm({ contract_type:'fulltime', hours_per_week:40, salary:'', start_date:'', end_date:'', cao:'', vacation_extra:0, notes:'' });
    }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true); setMsg('');
    const r = await saveContract(businessId, { user_id: selected, ...form });
    setSaving(false);
    if (r && !r.error) {
      setMsg(t('Contract opgeslagen ✓', 'Contract saved ✓'));
      setEditing(false);
      selectMember(selected);
      loadContracts();
    } else {
      setMsg(t('Fout bij opslaan', 'Error saving'));
    }
  };

  const remove = async () => {
    if (!selected || !window.confirm(t('Contract verwijderen?', 'Delete contract?'))) return;
    await deleteContract(businessId, selected);
    setContract(null); setEditing(false);
    loadContracts();
  };

  const wettelijk = calcWettelijk(form.hours_per_week || contract?.hours_per_week);

  return (
    <div>
      <h3 style={{ color: C.white, margin: '0 0 12px' }}>{t('Contracten', 'Contracts')}</h3>

      <MemberPicker members={members} value={selected} onChange={selectMember}
        placeholder={t('Kies medewerker…', 'Choose employee…')} />

      {selected && (
        <div style={{ marginTop: 16, background: _T.bg3, borderRadius: 12, padding: 16 }}>
          {!editing ? (
            <>
              {contract ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  <InfoRow label={t('Type', 'Type')} value={CONTRACT_TYPES.find(t => t.value === contract.contract_type)?.nl || contract.contract_type} />
                  <InfoRow label={t('Uren/week', 'Hours/week')} value={contract.hours_per_week} />
                  <InfoRow label={t('Salaris', 'Salary')} value={fmtMoney(contract.salary)} />
                  <InfoRow label={t('Startdatum', 'Start date')} value={fmt(contract.start_date)} />
                  <InfoRow label={t('Einddatum', 'End date')} value={fmt(contract.end_date)} />
                  <InfoRow label={t('CAO', 'CAO')} value={contract.cao || '—'} />
                  <InfoRow label={t('Wettelijke vakantiedagen/jaar', 'Statutory leave days/year')}
                    value={`${calcWettelijk(contract.hours_per_week)} dagen`} />
                  <InfoRow label={t('Bovenwettelijk verlof', 'Extra leave days')} value={`${contract.vacation_extra || 0} dagen`} />
                  {contract.notes && <div style={{ gridColumn: '1/-1', color: C.silver, fontSize: 13 }}>{contract.notes}</div>}
                </div>
              ) : (
                <p style={{ color: C.silver, margin: 0 }}>{t('Geen contract gevonden.', 'No contract found.')}</p>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button style={btnPrimary()} onClick={() => setEditing(true)}>
                    {contract ? t('Bewerken', 'Edit') : t('Contract aanmaken', 'Create contract')}
                  </button>
                  {contract && (
                    <button style={btnDanger()} onClick={remove}>
                      {t('Verwijderen', 'Delete')}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <ContractForm form={form} setForm={setForm} wettelijk={wettelijk}
              onSave={save} onCancel={() => setEditing(false)} saving={saving} />
          )}
          {msg && <p style={{ color: _T.accent, marginTop: 8, fontSize: 13 }}>{msg}</p>}
        </div>
      )}

      {/* Contracts list overview */}
      {contracts.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ color: C.silver, margin: '0 0 8px', fontSize: 13 }}>{t('Overzicht medewerkers met contract', 'Employees with contract')}</h4>
          {contracts.map(c => {
            const m = members.find(mm => String(mm.user_id) === String(c.user_id));
            return (
              <div key={c.id} onClick={() => selectMember(String(c.user_id))}
                style={{ background: _T.bg3, borderRadius: 10, padding: '10px 14px', marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: String(selected) === String(c.user_id) ? `1.5px solid ${_T.accent}` : `1px solid ${_T.cardBorderBot}` }}>
                <div>
                  <div style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{m?.display_name || m?.email || `User ${c.user_id}`}</div>
                  <div style={{ color: C.silver, fontSize: 12 }}>
                    {CONTRACT_TYPES.find(t => t.value === c.contract_type)?.nl} · {c.hours_per_week}u/w · {t('Wettelijk', 'Statutory')}: {calcWettelijk(c.hours_per_week)} {t('dagen', 'days')}
                  </div>
                </div>
                <div style={{ color: C.silver, fontSize: 12 }}>{fmt(c.start_date)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ color: C.silver, fontSize: 11, marginBottom: 2 }}>{label}</div>
      <div style={{ color: C.white, fontSize: 14, fontWeight: 500 }}>{value ?? '—'}</div>
    </div>
  );
}

function ContractForm({ form, setForm, wettelijk, onSave, onCancel, saving }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={labelStyle}>{t('Type contract', 'Contract type')}</label>
      <select value={form.contract_type} onChange={e => set('contract_type', e.target.value)} style={inputStyle()}>
        {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.nl}</option>)}
      </select>

      <label style={labelStyle}>{t('Uren per week', 'Hours per week')}</label>
      <input type="number" min="1" max="80" value={form.hours_per_week} onChange={e => set('hours_per_week', +e.target.value)} style={inputStyle()} />

      <div style={{ background: `${_T.accent}15`, borderRadius: 8, padding: '8px 12px', fontSize: 13, color: _T.accent }}>
        ℹ️ {t(`Wettelijke vakantiedagen: ${wettelijk} dagen/jaar (4 × ${form.hours_per_week}/8)`, `Statutory leave: ${wettelijk} days/year (4 × ${form.hours_per_week}/8)`)}
      </div>

      <label style={labelStyle}>{t('Bovenwettelijk verlof (extra dagen/jaar)', 'Extra leave days/year (above statutory)')}</label>
      <input type="number" min="0" value={form.vacation_extra} onChange={e => set('vacation_extra', +e.target.value)} style={inputStyle()} />

      <label style={labelStyle}>{t('Bruto salaris / maand (€)', 'Gross salary / month (€)')}</label>
      <input type="number" min="0" step="0.01" value={form.salary} onChange={e => set('salary', e.target.value)} style={inputStyle()} placeholder="0.00" />

      <label style={labelStyle}>{t('CAO', 'CAO')}</label>
      <input type="text" value={form.cao} onChange={e => set('cao', e.target.value)} style={inputStyle()} placeholder={t('bijv. CAO Zorg & Welzijn', 'e.g. CAO Healthcare')} />

      <label style={labelStyle}>{t('Startdatum', 'Start date')}</label>
      <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inputStyle()} />

      <label style={labelStyle}>{t('Einddatum (optioneel)', 'End date (optional)')}</label>
      <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={inputStyle()} />

      <label style={labelStyle}>{t('Notities', 'Notes')}</label>
      <textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={{...inputStyle(), minHeight: 60}} />

      <div style={{ display: 'flex', gap: 8 }}>
        <button style={btnPrimary()} onClick={onSave} disabled={saving}>{saving ? t('Opslaan…', 'Saving…') : t('Opslaan', 'Save')}</button>
        <button style={{...btnGhost()}} onClick={onCancel}>{t('Annuleren', 'Cancel')}</button>
      </div>
    </div>
  );
}

// ─── Tab: Verlof ─────────────────────────────────────────────────────────────
function LeaveTab({ businessId, myRole, myUid, members }) {
  const [requests, setRequests]  = useState([]);
  const [showForm, setShowForm]  = useState(false);
  const [filterUid, setFilterUid]= useState('');
  const [form, setForm]          = useState({ user_id:'', leave_type:'vacation', date_from:'', date_to:'', note:'' });
  const [saving, setSaving]      = useState(false);
  const [msg, setMsg]            = useState('');

  const canManage = ['owner','admin','hr_manager','accountant','bookkeeper'].includes(myRole);

  const load = useCallback(async () => {
    const params = {};
    if (filterUid) params.user_id = filterUid;
    const r = await getLeaveRequests(businessId, Object.keys(params).length ? params : null);
    if (Array.isArray(r)) setRequests(r);
  }, [businessId, filterUid]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true); setMsg('');
    const payload = { ...form };
    if (!canManage) payload.user_id = myUid;
    const r = await submitLeaveRequest(businessId, payload);
    setSaving(false);
    if (r && !r.error) { setMsg(t('Aanvraag ingediend ✓', 'Request submitted ✓')); setShowForm(false); load(); }
    else setMsg(r?.error || t('Fout', 'Error'));
  };

  const review = async (lid, status) => {
    await reviewLeaveRequest(businessId, lid, { status });
    load();
  };

  const remove = async (lid) => {
    if (!window.confirm(t('Aanvraag verwijderen?', 'Delete request?'))) return;
    await deleteLeaveRequest(businessId, lid);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ color: C.white, margin: 0 }}>{t('Verlofaanvragen', 'Leave requests')}</h3>
        <button style={btnPrimary()} onClick={() => { setShowForm(!showForm); setMsg(''); }}>
          {showForm ? t('Sluiten', 'Close') : '+ ' + t('Aanvragen', 'Request')}
        </button>
      </div>

      {canManage && (
        <div style={{ marginBottom: 12 }}>
          <MemberPicker members={members} value={filterUid} onChange={setFilterUid}
            placeholder={t('Filter op medewerker…', 'Filter by employee…')} />
        </div>
      )}

      {showForm && (
        <div style={{ background: _T.bg3, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <h4 style={{ color: C.white, margin: '0 0 12px' }}>{t('Verlof aanvragen', 'Request leave')}</h4>
          {canManage && (
            <MemberPicker members={members} value={form.user_id} onChange={v => setForm(f => ({ ...f, user_id: v }))}
              placeholder={t('Voor wie?', 'For whom?')} />
          )}
          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>{t('Type verlof', 'Leave type')}</label>
            <select value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))} style={{...inputStyle(), marginTop: 4}}>
              {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.nl}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
            <div>
              <label style={labelStyle}>{t('Van', 'From')}</label>
              <input type="date" value={form.date_from} onChange={e => setForm(f => ({ ...f, date_from: e.target.value }))} style={{...inputStyle(), marginTop: 4}} />
            </div>
            <div>
              <label style={labelStyle}>{t('Tot', 'To')}</label>
              <input type="date" value={form.date_to} onChange={e => setForm(f => ({ ...f, date_to: e.target.value }))} style={{...inputStyle(), marginTop: 4}} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>{t('Toelichting (optioneel)', 'Note (optional)')}</label>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{...inputStyle(), marginTop: 4, minHeight: 50}} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary()} onClick={submit} disabled={saving}>{saving ? t('Opslaan…', 'Saving…') : t('Indienen', 'Submit')}</button>
            <button style={{...btnGhost()}} onClick={() => setShowForm(false)}>{t('Annuleren', 'Cancel')}</button>
          </div>
          {msg && <p style={{ color: _T.accent, marginTop: 8, fontSize: 13 }}>{msg}</p>}
        </div>
      )}

      {requests.length === 0 && <p style={{ color: C.silver }}>{t('Geen aanvragen.', 'No requests.')}</p>}

      {requests.map(req => {
        const m = members.find(mm => String(mm.user_id) === String(req.user_id));
        const leaveLabel = LEAVE_TYPES.find(t => t.value === req.leave_type)?.nl || req.leave_type;
        const isOwn = String(req.user_id) === String(myUid);
        return (
          <div key={req.id} style={{ background: _T.bg3, borderRadius: 12, padding: 14, marginBottom: 10, borderLeft: `4px solid ${LEAVE_STATUS_COLORS[req.status] || C.silver}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{m?.display_name || m?.email || `User ${req.user_id}`}</div>
                <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{leaveLabel} · {fmt(req.date_from)} – {fmt(req.date_to)} · {req.days} {t('dag(en)', 'day(s)')}</div>
                {req.note && <div style={{ color: C.silver, fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>{req.note}</div>}
              </div>
              <span style={{ background: LEAVE_STATUS_COLORS[req.status], color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {req.status === 'pending' ? t('Wacht', 'Pending') : req.status === 'approved' ? t('Goedgekeurd', 'Approved') : t('Afgewezen', 'Declined')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {canManage && req.status === 'pending' && (
                <>
                  <button style={{...btnPrimary({ padding: '4px 12px', fontSize: 12, background: '#10b981' })}} onClick={() => review(req.id, 'approved')}>{t('Goedkeuren', 'Approve')}</button>
                  <button style={{...btnPrimary({ padding: '4px 12px', fontSize: 12, background: '#ef4444' })}} onClick={() => review(req.id, 'declined')}>{t('Afwijzen', 'Decline')}</button>
                </>
              )}
              {(canManage || isOwn) && (
                <button style={{...btnGhost({ padding: '4px 12px', fontSize: 12 })}} onClick={() => remove(req.id)}>{t('Verwijderen', 'Delete')}</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Ziekmeldingen ───────────────────────────────────────────────────────
function SickTab({ businessId, myRole, myUid, members }) {
  const [reports, setReports]  = useState([]);
  const [showForm, setShowForm]= useState(false);
  const [form, setForm]        = useState({ user_id: '', note: '' });
  const [saving, setSaving]    = useState(false);
  const [msg, setMsg]          = useState('');

  const canManage = ['owner','admin','hr_manager','accountant','bookkeeper'].includes(myRole);

  const load = useCallback(async () => {
    const r = await getSickReports(businessId);
    if (Array.isArray(r)) setReports(r);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true); setMsg('');
    const payload = { ...form };
    if (!canManage) payload.user_id = myUid;
    const r = await fileSickReport(businessId, payload);
    setSaving(false);
    if (r && !r.error) { setMsg(t('Ziekmelding ingediend ✓', 'Sick report filed ✓')); setShowForm(false); load(); }
    else setMsg(r?.error || t('Fout', 'Error'));
  };

  const recover = async (sid) => {
    const note = window.prompt(t('Hersteld – eventuele toelichting:', 'Recovered – optional note:'), '');
    if (note === null) return;
    await markRecovered(businessId, sid, { note });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ color: C.white, margin: 0 }}>{t('Ziekmeldingen', 'Sick reports')}</h3>
        <button style={btnPrimary()} onClick={() => { setShowForm(!showForm); setMsg(''); }}>
          {showForm ? t('Sluiten', 'Close') : '+ ' + t('Ziekmelding', 'Report sick')}
        </button>
      </div>

      {showForm && (
        <div style={{ background: _T.bg3, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          {canManage && (
            <div style={{ marginBottom: 10 }}>
              <MemberPicker members={members} value={form.user_id} onChange={v => setForm(f => ({ ...f, user_id: v }))}
                placeholder={t('Voor wie?', 'For whom?')} />
            </div>
          )}
          <label style={labelStyle}>{t('Toelichting (optioneel)', 'Note (optional)')}</label>
          <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{...inputStyle(), marginTop: 4, minHeight: 50}} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary()} onClick={submit} disabled={saving}>{saving ? t('Opslaan…', 'Saving…') : t('Indienen', 'Submit')}</button>
            <button style={{...btnGhost()}} onClick={() => setShowForm(false)}>{t('Annuleren', 'Cancel')}</button>
          </div>
          {msg && <p style={{ color: _T.accent, marginTop: 8, fontSize: 13 }}>{msg}</p>}
        </div>
      )}

      {reports.length === 0 && <p style={{ color: C.silver }}>{t('Geen ziekmeldingen.', 'No sick reports.')}</p>}

      {reports.map(rep => {
        const m = members.find(mm => String(mm.user_id) === String(rep.user_id));
        const active = !rep.recovered_at;
        return (
          <div key={rep.id} style={{ background: _T.bg3, borderRadius: 12, padding: 14, marginBottom: 10, borderLeft: `4px solid ${active ? '#ef4444' : '#10b981'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{m?.display_name || m?.email || `User ${rep.user_id}`}</div>
                <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
                  {t('Ziek sinds', 'Sick since')}: {fmt(rep.reported_at)}
                  {rep.recovered_at && ` · ${t('Hersteld', 'Recovered')}: ${fmt(rep.recovered_at)}`}
                </div>
                {rep.note && <div style={{ color: C.silver, fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>{rep.note}</div>}
              </div>
              <span style={{ background: active ? '#ef444420' : '#10b98120', color: active ? '#ef4444' : '#10b981', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                {active ? t('Ziek', 'Sick') : t('Hersteld', 'Recovered')}
              </span>
            </div>
            {active && canManage && (
              <button style={{...btnPrimary({ padding: '4px 12px', fontSize: 12, marginTop: 10, background: '#10b981' })}} onClick={() => recover(rep.id)}>
                {t('Markeer hersteld', 'Mark recovered')}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Verlofsaldo ─────────────────────────────────────────────────────────
function BalanceTab({ businessId, members }) {
  const [year, setYear]      = useState(new Date().getFullYear());
  const [balances, setBalances] = useState([]);
  const [loading, setLoading]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getLeaveBalance(businessId, year);
    setLoading(false);
    if (Array.isArray(r)) setBalances(r);
  }, [businessId, year]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ color: C.white, margin: 0 }}>{t('Verlofsaldo', 'Leave balance')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={{...btnPrimary({ padding: '4px 10px' })}} onClick={() => setYear(y => y - 1)}>‹</button>
          <span style={{ color: C.white, fontWeight: 600 }}>{year}</span>
          <button style={{...btnPrimary({ padding: '4px 10px' })}} onClick={() => setYear(y => y + 1)}>›</button>
        </div>
      </div>

      {loading && <p style={{ color: C.silver }}>{t('Laden…', 'Loading…')}</p>}

      {!loading && balances.length === 0 && (
        <p style={{ color: C.silver }}>{t('Geen contracten of data gevonden.', 'No contracts or data found.')}</p>
      )}

      {/* Table */}
      {!loading && balances.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${_T.cardBorderBot}` }}>
                {[t('Medewerker','Employee'), t('Uren/w','Hrs/w'), t('Wettelijk','Statutory'), t('Bovenwettelijk','Extra'), t('Totaal','Total'), t('Opgenomen','Used'), t('Saldo','Balance')].map((h,i) => (
                  <th key={i} style={{ color: C.silver, fontWeight: 600, padding: '6px 8px', textAlign: i === 0 ? 'left' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {balances.map(b => {
                const m = members.find(mm => String(mm.user_id) === String(b.user_id));
                const balColor = b.balance < 0 ? '#ef4444' : b.balance < 2 ? '#f59e0b' : '#10b981';
                return (
                  <tr key={b.user_id} style={{ borderBottom: `1px solid ${_T.cardBorderBot}20` }}>
                    <td style={{ color: C.white, padding: '8px', fontWeight: 500 }}>{m?.display_name || m?.email || `User ${b.user_id}`}</td>
                    <td style={tdStyle}>{b.hours_per_week}</td>
                    <td style={tdStyle}>{b.wettelijk}</td>
                    <td style={tdStyle}>{b.bovenwettelijk}</td>
                    <td style={tdStyle}>{b.total}</td>
                    <td style={tdStyle}>{b.used}</td>
                    <td style={{ ...tdStyle, color: balColor, fontWeight: 700 }}>{b.balance}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 16, background: `${_T.accent}10`, borderRadius: 10, padding: 12, fontSize: 12, color: C.silver }}>
        <strong style={{ color: C.white }}>ℹ️ {t('CAO toelichting', 'CAO explanation')}</strong><br />
        {t(
          'Wettelijke vakantiedagen = 4 × (uren per week ÷ 8). Bij 40u/w = 20 dagen. Bovenwettelijk = extra contractuele dagen. Saldo = Totaal − Opgenomen vakantiedagen dit jaar.',
          'Statutory leave = 4 × (hours/week ÷ 8). At 40h/w = 20 days. Extra = contractual days above statutory. Balance = Total − Used vacation days this year.'
        )}
      </div>
    </div>
  );
}

const tdStyle = { color: C.silver, padding: '8px', textAlign: 'right' };

// ─── Tab: HR-rollen ───────────────────────────────────────────────────────────
function HrRolesTab({ businessId, myRole, members, onMembersChange }) {
  const [saving, setSaving] = useState({});
  const [msg, setMsg]       = useState('');

  const canEdit = ['owner','admin'].includes(myRole);

  const changeHrRole = async (uid, hrRole) => {
    setSaving(s => ({ ...s, [uid]: true }));
    await updateMemberHrRole(businessId, uid, hrRole || null);
    setSaving(s => ({ ...s, [uid]: false }));
    setMsg(t('Rol bijgewerkt ✓', 'Role updated ✓'));
    if (onMembersChange) onMembersChange();
    setTimeout(() => setMsg(''), 2000);
  };

  return (
    <div>
      <h3 style={{ color: C.white, margin: '0 0 12px' }}>{t('HR-toegang & rollen', 'HR access & roles')}</h3>
      <p style={{ color: C.silver, fontSize: 13, margin: '0 0 16px' }}>
        {t('Geef externe accountants of HR-managers toegang tot de HR-module.', 'Grant external accountants or HR managers access to the HR module.')}
      </p>

      {members.map(m => (
        <div key={m.user_id} style={{ background: _T.bg3, borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{m.display_name || m.email}</div>
            <div style={{ color: C.silver, fontSize: 12 }}>{m.role}</div>
          </div>
          {canEdit ? (
            <select
              value={m.hr_role || ''}
              disabled={!!saving[m.user_id]}
              onChange={e => changeHrRole(m.user_id, e.target.value)}
              style={{...inputStyle({ width: 'auto', fontSize: 12, padding: '4px 8px' })}}
            >
              {HR_ROLES.map(r => <option key={r.value} value={r.value}>{r.nl}</option>)}
            </select>
          ) : (
            <span style={{ color: C.silver, fontSize: 12 }}>{HR_ROLES.find(r => r.value === (m.hr_role || ''))?.nl}</span>
          )}
        </div>
      ))}

      {msg && <p style={{ color: '#10b981', marginTop: 8, fontSize: 13 }}>{msg}</p>}
    </div>
  );
}

// ─── Main BusinessHRScreen ────────────────────────────────────────────────────
export default function BusinessHRScreen({ businessId, myRole, myUid, activeTab, lang: langProp }) {
  _isNl = langProp === 'nl';
  const nl = _isNl;
  const [members, setMembers] = useState([]);

  const loadMembers = useCallback(async () => {
    const r = await getMembers(businessId);
    if (Array.isArray(r)) setMembers(r);
  }, [businessId]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const tab = activeTab || 'contracts';

  return (
    <div>
      {tab === 'contracts' && <ContractTab businessId={businessId} myRole={myRole} members={members} />}
      {tab === 'leave'     && <LeaveTab    businessId={businessId} myRole={myRole} myUid={myUid} members={members} />}
      {tab === 'sick'      && <SickTab     businessId={businessId} myRole={myRole} myUid={myUid} members={members} />}
      {tab === 'balance'   && <BalanceTab  businessId={businessId} members={members} />}
      {tab === 'hrroles'   && <HrRolesTab  businessId={businessId} myRole={myRole} members={members} onMembersChange={loadMembers} />}
    </div>
  );
}
